import { useEffect, useState, type FormEvent, type MouseEvent } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { Bookmark, UUID } from "../sync/types";
import { ChannelArtifacts } from "./ArtifactView";
import { IconEdit, IconLink, IconTrash } from "./icons";
import { PopMenu, type MenuItem } from "./PopMenu";
import { Form, Sheet } from "./ui";
import { t } from "../i18n";

// hostOf is a link's host, as a bookmark's name when it has none.
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

// ChannelBar runs under a channel's header: its bookmarks, then the
// documents agents shared in it, and a way to add a bookmark for those who
// can.
export function ChannelBar({ channel, canEdit }: { channel: UUID; canEdit: boolean }) {
  const session = useSession();
  const state = useLantern();
  const [menu, setMenu] = useState<{ at: DOMRect; b: Bookmark } | null>(null);
  const [editing, setEditing] = useState<Bookmark | "new" | null>(null);
  useEffect(() => {
    void session.loadBookmarks(channel).catch(() => {});
  }, [session, channel]);
  const list = state.bookmarks.get(channel) ?? [];

  function open(e: MouseEvent, b: Bookmark) {
    // A link to a place here opens in place.
    if (b.url.startsWith(location.origin + "/") && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      navigate(b.url.slice(location.origin.length));
    }
  }
  const items = (b: Bookmark): MenuItem[] => [
    { label: t("Copy link"), icon: <IconLink size={16} />, run: () => void navigator.clipboard?.writeText(b.url).catch(() => {}) },
    ...(canEdit
      ? [
          { label: t("Edit"), icon: <IconEdit size={16} />, run: () => setEditing(b) },
          { label: t("Remove"), icon: <IconTrash size={16} />, danger: true, divider: true, run: () => void session.api.removeBookmark(channel, b.id).catch(() => {}) },
        ]
      : []),
  ];

  return (
    <div className="channel-artifacts" aria-label={t("Bookmarks and documents")}>
      {list.map((b) => (
        <span key={b.id} className="artifact-chip bookmark">
          <a
            href={b.url}
            target="_blank"
            rel="noopener noreferrer"
            title={b.url}
            onClick={(e) => open(e, b)}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu({ at: new DOMRect(e.clientX, e.clientY, 0, 0), b });
            }}
          >
            🔗 {b.title}
          </a>
          <button className="bookmark-more" aria-label={t("Bookmark options: {title}", { title: b.title })} title={t("Options")} onClick={(e) => setMenu({ at: e.currentTarget.getBoundingClientRect(), b })}>
            ⋯
          </button>
        </span>
      ))}
      <ChannelArtifacts channel={channel} />
      {canEdit && (
        <button className="artifact-chip add-bookmark" onClick={() => setEditing("new")} title={t("Add a bookmark")} aria-label={t("Add a bookmark")}>
          + {list.length ? "" : t("Add a bookmark")}
        </button>
      )}
      {menu && <PopMenu anchor={menu.at} label={t("Bookmark: {title}", { title: menu.b.title })} items={items(menu.b)} onClose={() => setMenu(null)} />}
      {editing && <BookmarkSheet channel={channel} bookmark={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// BookmarkSheet adds a bookmark to a channel, or edits one.
function BookmarkSheet({ channel, bookmark, onClose }: { channel: UUID; bookmark?: Bookmark; onClose: () => void }) {
  const session = useSession();
  const [url, setURL] = useState(bookmark?.url ?? "");
  const [title, setTitle] = useState(bookmark?.title ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const link = /^[a-z][a-z0-9+.-]*:/i.test(url.trim()) ? url.trim() : url.trim() && `https://${url.trim()}`;

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!link) return;
    setError("");
    setBusy(true);
    try {
      const b = { title: title.trim(), url: link };
      if (bookmark) await session.api.updateBookmark(channel, bookmark.id, b);
      else await session.api.addBookmark(channel, b);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }
  return (
    <Sheet title={bookmark ? t("Edit bookmark") : t("Add a bookmark")} onClose={onClose} className="bookmark-sheet">
      <Form onSubmit={save}>
        <label>
          {t("Link")}
          <input value={url} onChange={(e) => setURL(e.target.value)} placeholder="https://wiki.example/runbook" autoFocus inputMode="url" spellCheck={false} maxLength={2000} />
        </label>
        <label>
          {t("Name")}
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={hostOf(link) || t("Runbook")} maxLength={100} />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button type="button" className="secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button type="submit" disabled={busy || !link}>
            {bookmark ? t("Save") : t("Add")}
          </button>
        </div>
      </Form>
    </Sheet>
  );
}
